import {
  Room,
  RoomEvent,
  VideoPresets,
  ScreenSharePresets,
  RemoteTrack,
  RemoteParticipant,
  Participant,
  ConnectionQuality,
  RemoteTrackPublication,
  LocalTrackPublication,
  LocalParticipant,
  TrackPublication,
  Track,
  DisconnectReason,
} from 'livekit-client';
import { createEncryptedRoom } from './e2ee-runtime.js';
import { importConferenceDataKey, encryptConferenceData, decryptConferenceData } from './e2ee-data.js';

/** Capture a chosen surface while keeping the meeting in focus when the browser supports it. */
export async function captureDisplayMedia(options: any): Promise<MediaStream> {
  let controller: any = null;
  try {
    const Controller = (window as any).CaptureController;
    if (typeof Controller === 'function') {
      controller = new Controller();
      controller.setFocusBehavior('no-focus-change');
    }
  } catch (_) { controller = null; }
  return navigator.mediaDevices.getDisplayMedia(controller ? { ...options, controller } : options);
}

export interface ChatMessage {
  id: string;
  senderName: string;
  senderIdentity: string;
  text: string;
  timestamp: string;
  isSelf: boolean;
  replyTo?: { id: string; senderName: string; text: string };
  file?: {
    name: string;
    size: number;
    type: string;
    dataUrl: string;
    progress?: number;
  };
}

export interface LiveKitCallbacks {
  onParticipantConnected?: (p: RemoteParticipant) => void;
  onLocalPermissionsChanged?: () => void;
  onParticipantDisconnected?: (p: RemoteParticipant) => void;
  onTrackSubscribed?: (track: RemoteTrack, publication: RemoteTrackPublication, participant: RemoteParticipant) => void;
  onTrackUnsubscribed?: (track: RemoteTrack, publication: RemoteTrackPublication, participant: RemoteParticipant) => void;
  onTrackMuted?: (publication: TrackPublication, participant: Participant) => void;
  onTrackUnmuted?: (publication: TrackPublication, participant: Participant) => void;
  onActiveSpeakersChanged?: (speakers: Participant[]) => void;
  onConnectionQualityChanged?: (quality: ConnectionQuality, p: Participant) => void;
  onChatMessage?: (message: ChatMessage) => void;
  onChatReaction?: (data: { messageId: string; emoji: string; active: boolean; identity: string }) => void;
  onChatFile?: (message: ChatMessage) => void;
  onFileTransferProgress?: (info: {
    transferId: string;
    progress: number;
    name: string;
    size: number;
    senderName: string;
    senderIdentity: string;
    timestamp: string;
    text: string;
    isComplete: boolean;
  }) => void;
  onModerationMessage?: (data: { action: string; targetIdentity?: string; targetName?: string; senderIdentity?: string; newName?: string }) => void;
  onHandRaise?: (data: { identity: string; isRaised: boolean; participantName: string }) => void;
  onSessionHistory?: (messages: ChatMessage[]) => void;
  onReaction?: (data: { identity: string; emoji: string; participantName: string }) => void;
  onAudioPlaybackChanged?: (canPlayback: boolean) => void;
  onTrackPublished?: (publication: RemoteTrackPublication, participant: RemoteParticipant) => void;
  onTrackUnpublished?: (publication: RemoteTrackPublication, participant: RemoteParticipant) => void;
  onLocalTrackPublished?: (publication: LocalTrackPublication, participant: LocalParticipant) => void;
  onLocalTrackUnpublished?: (publication: LocalTrackPublication, participant: LocalParticipant) => void;
  onScreenShareStatus?: (data: { isSharing: boolean; identity: string; sharerName: string }) => void;
  onRecordingStatus?: (data: { isRecording: boolean; recorderName: string; identity: string; recordingSeconds?: number }) => void;
  onQueryRecordingStatus?: (data: { fromIdentity: string; fromName?: string }) => void;
  onRecordingCommand?: (data: { action: string; requestedBy: string }) => void;
  onWhiteboardDraw?: (data: { x0: number; y0: number; x1: number; y1: number; color: string; size: number; tool: string }) => void;
  onWhiteboardClear?: () => void;
  onWhiteboardToggle?: (data: { isOpen: boolean; senderName: string }) => void;
  onPollLaunch?: (data: { pollId: string; question: string; options: string[]; creatorName: string; creatorIdentity: string }) => void;
  onPollVote?: (data: { pollId: string; optionIndex: number; voterName: string; voterIdentity: string }) => void;
  onPollClose?: (data: { pollId: string; senderIdentity: string }) => void;
  onWaitingRoomKnock?: (data: { identity: string; name: string; timestamp: number }) => void;
  onWaitingRoomDecision?: (data: { targetIdentity: string; decision: 'admitted' | 'rejected' }) => void;
  onWaitingRoomMoveBack?: (data: { targetIdentity: string }) => void;
  onHostStatus?: (data: { hostConnected: boolean; hostName: string }) => void;
  onMeetingEndedByHost?: (data: { hostName?: string; reason?: string }) => void;
  onAdminLayoutSync?: (data: { viewMode: 'gallery' | 'speaker' | 'studio'; pinnedIdentity: string | null; pinnedName?: string; adminName?: string; isStageLocked?: boolean; senderIdentity?: string; isPrimaryHost?: boolean }) => void;
  onReconnecting?: () => void;
  onReconnected?: () => void;
  onDisconnected?: (reason?: DisconnectReason) => void;
  onEncryptionViolation?: () => void;
  onRemoteEncryptionProblem?: () => void;
  onLocalScreenStreamReady?: (stream: MediaStream) => void;
  onLocalScreenShareEnded?: () => void;
  onScreenShareChanged?: (isSharing: boolean) => void;
}

export interface WebRTCStatsSummary {
  dtlsVersions?: string[];
  connected: boolean;
  connectionState: string;
  rttMs: number;
  outboundBitrateKbps: number;
  inboundBitrateKbps: number;
  outboundResolution?: string;
  outboundFps?: number;
  inboundResolution?: string;
  inboundFps?: number;
  packetLossPercent: number;
  qualityLimitation?: string;
  audioCodec?: string;
  videoCodec?: string;
  serverUrl: string;
}

export class LiveKitClientManager {
  private room: Room | null = null;
  private disposeEncryptionWorker: (() => void) | null = null;
  private encryptionRequired = false;
  private conferenceDataKey: CryptoKey | null = null;
  private encryptionViolation = false;
  private lastRemoteEncryptionWarning = 0;
  private quarantinedParticipants = new Set<string>();
  private callbacks: LiveKitCallbacks = {};
  public displayName: string = '';
  public isCameraEnabled = false;
  public isMicEnabled = false;
  public isScreenShareEnabled = false;
  public currentFacingMode: 'user' | 'environment' = 'user';
  private localCameraStream: MediaStream | null = null;
  private localMicStream: MediaStream | null = null;
  private prevStatsTime: number = 0;
  private prevBytesSent: number = 0;
  private prevBytesReceived: number = 0;
  private lastOutboundBitrateKbps: number = 0;
  private lastInboundBitrateKbps: number = 0;
  private localScreenStream: MediaStream | null = null;
  private cachedCameraStream: MediaStream | null = null;
  private cachedCameraMst: MediaStreamTrack | null = null;
  private cachedMicStream: MediaStream | null = null;
  private cachedMicMst: MediaStreamTrack | null = null;
  private cachedScreenStream: MediaStream | null = null;
  private cachedScreenMst: MediaStreamTrack | null = null;
  private screenStopTask: Promise<void> | null = null;

  private screenVideoCodec(): 'h264' | 'vp8' {
    try {
      const codecs = RTCRtpSender.getCapabilities('video')?.codecs || [];
      if (codecs.some(codec => codec.mimeType.toLowerCase() === 'video/h264')) return 'h264';
    } catch (_) {}
    return 'vp8';
  }
  private fallbackInterval: any = null;
  private incomingFileTransfers = new Map<string, {
    chunks: string[];
    receivedCount: number;
    totalChunks: number;
    metadata: {
      name: string;
      size: number;
      fileType: string;
      text: string;
      timestamp: string;
      senderName: string;
      senderIdentity: string;
    };
    timeoutId: any;
  }>();

  constructor(callbacks: LiveKitCallbacks = {}) {
    this.callbacks = callbacks;
  }

  public getRoom(): Room | null {
    return this.room;
  }

  public getLocalCameraStream(): MediaStream | null {
    if (this.room && this.room.state === 'connected') {
      try {
        let pub = this.room.localParticipant.getTrackPublication(Track.Source.Camera);
        if (!pub || !pub.track) {
          for (const p of this.room.localParticipant.videoTrackPublications.values()) {
            if (p.source !== Track.Source.ScreenShare && p.trackName !== 'screen_share' && (p as any).name !== 'screen_share' && p.track) {
              pub = p;
              break;
            }
          }
        }
        if (pub && pub.track) {
          const mst = (pub.track as any).mediaStreamTrack as MediaStreamTrack | undefined;
          if (mst && mst.readyState === 'live') {
            if (this.cachedCameraStream && this.cachedCameraMst === mst && this.cachedCameraStream.active) {
              return this.cachedCameraStream;
            }
            this.cachedCameraMst = mst;
            this.cachedCameraStream = new MediaStream([mst]);
            return this.cachedCameraStream;
          }
        }
      } catch (e) {
        console.warn('[LiveKitClient] getLocalCameraStream error:', e);
      }
    }
    if (this.localCameraStream && this.localCameraStream.active) {
      return this.localCameraStream;
    }
    return null;
  }

  public getLocalMicStream(): MediaStream | null {
    if (this.room && this.room.state === 'connected') {
      try {
        const pub = this.room.localParticipant.getTrackPublication(Track.Source.Microphone);
        if (pub && pub.track) {
          const mst = (pub.track as any).mediaStreamTrack as MediaStreamTrack | undefined;
          if (mst && mst.readyState === 'live') {
            if (this.cachedMicStream && this.cachedMicMst === mst && this.cachedMicStream.active) {
              return this.cachedMicStream;
            }
            this.cachedMicMst = mst;
            this.cachedMicStream = new MediaStream([mst]);
            return this.cachedMicStream;
          }
        }
      } catch (e) {
        console.warn('[LiveKitClient] getLocalMicStream error:', e);
      }
    }
    if (this.localMicStream && this.localMicStream.active) {
      return this.localMicStream;
    }
    return null;
  }

  public getLocalScreenStream(): MediaStream | null {
    if (this.localScreenStream) {
      const tr = this.localScreenStream.getVideoTracks()[0];
      if (tr && tr.readyState === 'live') {
        return this.localScreenStream;
      }
    }
    if (this.cachedScreenStream && this.cachedScreenMst && this.cachedScreenMst.readyState === 'live') {
      return this.cachedScreenStream;
    }
    if (this.room && this.room.state === 'connected') {
      try {
        const pub = this.room.localParticipant.getTrackPublication(Track.Source.ScreenShare);
        if (pub && pub.track) {
          const mst = (pub.track as any).mediaStreamTrack as MediaStreamTrack | undefined;
          if (mst && mst.readyState === 'live') {
            this.cachedScreenMst = mst;
            this.cachedScreenStream = new MediaStream([mst]);
            this.localScreenStream = this.cachedScreenStream;
            return this.cachedScreenStream;
          }
        }
      } catch (e) {
        console.warn('[LiveKitClient] getLocalScreenStream error:', e);
      }
    }
    return null;
  }

  public async switchDevice(kind: MediaDeviceKind, deviceId: string): Promise<boolean> {
    if (this.room && this.room.state === 'connected') {
      try {
        const switched = await this.room.switchActiveDevice(kind, deviceId, true);
        if (!switched) return false;
        if (kind === 'videoinput') {
          this.cachedCameraMst = null;
          this.cachedCameraStream = null;
          this.localCameraStream = null;
        }
        return true;
      } catch (err) {
        console.warn(`[LiveKitClient] switchActiveDevice(${kind}) error:`, err);
        return false;
      }
    }
    return false;
  }

  public async connect(url: string, token: string, encryptionKey?: Uint8Array): Promise<Room> {
    this.encryptionRequired = encryptionKey !== undefined;
    this.quarantinedParticipants.clear();
    this.lastRemoteEncryptionWarning = 0;
    this.conferenceDataKey = encryptionKey ? await importConferenceDataKey(encryptionKey) : null;
    this.encryptionViolation = false;
    const roomOptions = {
      adaptiveStream: false,
      dynacast: false,
      reconnectPolicy: {
        // Aggressive reconnection: retry up to 15 times with increasing delay
        nextRetryDelayInMs: (context: any) => {
          if (context.retryCount > 15) return null; // give up after 15
          return Math.min(500 * Math.pow(1.5, context.retryCount), 10000);
        },
      },
      videoCaptureDefaults: {
        resolution: VideoPresets.h720.resolution,
      },
      publishDefaults: {
        videoEncoding: VideoPresets.h720.encoding,
        simulcast: false,
        screenShareEncoding: ScreenSharePresets.h1080fps30.encoding,
        screenShareSimulcastLayers: [],
        dtx: true,
        red: false,
      },
      audioCaptureDefaults: {
        autoGainControl: true,
        echoCancellation: true,
        noiseSuppression: true,
      },
    };
    if (encryptionKey) {
      const encrypted = await createEncryptedRoom(encryptionKey, roomOptions);
      this.room = encrypted.room;
      this.disposeEncryptionWorker = encrypted.dispose;
    } else {
      this.room = new Room(roomOptions);
    }

    this.setupListeners();

    // Do NOT override iceServers with custom rtcConfig so LiveKit Server's own TURN server (217.217.233.20:3478) is used
    try {
      await this.room.connect(url, token, { autoSubscribe: true });
      if (this.encryptionViolation || !this.room) throw new Error('Conferința conține un flux necriptat.');
      if (this.encryptionRequired) {
        await this.room.setE2EEEnabled(true);
        for (let attempt = 0; attempt < 50 && this.room && !this.room.isE2EEEnabled; attempt++) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        if (!this.room?.isE2EEEnabled || this.encryptionViolation) throw new Error('Criptarea canalului de date nu a putut fi confirmată.');
      }
    } catch (error) {
      await this.abortConnection();
      throw error;
    }
    try {
      await this.room.startAudio();
    } catch (e) {
      console.warn('[LiveKitClient] startAudio initial call:', e);
    }

    // Attach global user interaction listeners to immediately unlock audio if browser autoplay blocked it (crucial for PWA, tablets and mobile)
    const unlockAudio = async () => {
      if (this.room && !this.room.canPlaybackAudio) {
        try {
          await this.room.startAudio();
        } catch (_) {}
      }
      if (typeof document !== 'undefined') {
        document.querySelectorAll('audio').forEach((a: any) => {
          if (a.paused && !a.muted) {
            a.play().catch(() => {});
          }
        });
      }
    };
    if (typeof window !== 'undefined') {
      ['touchstart', 'touchend', 'pointerdown', 'click', 'keydown'].forEach(evt => {
        window.addEventListener(evt, unlockAudio, { passive: true });
      });
    }
    return this.room;
  }

  private rejectUnencryptedTrack(): void {
    if (this.encryptionViolation) return;
    this.encryptionViolation = true;
    this.callbacks.onEncryptionViolation?.();
    void this.abortConnection();
  }

  private rejectRemoteTrack(pub: RemoteTrackPublication): void {
    pub.setSubscribed(false);
    const now = Date.now();
    if (now - this.lastRemoteEncryptionWarning > 10000) {
      this.lastRemoteEncryptionWarning = now;
      this.callbacks.onRemoteEncryptionProblem?.();
    }
  }

  private setupListeners() {
    if (!this.room) return;

    this.room
      .on(RoomEvent.EncryptionError, (_error, participant) => {
        if (!this.encryptionRequired) return;
        const remote = participant && this.room?.remoteParticipants.get(participant.identity);
        if (remote && remote === participant) {
          this.quarantinedParticipants.add(remote.identity);
          for (const pub of remote.trackPublications.values()) this.rejectRemoteTrack(pub);
        } else {
          // An unidentified or local cryptor failure cannot be safely isolated.
          this.rejectUnencryptedTrack();
        }
      })
      .on(RoomEvent.ParticipantPermissionsChanged, (_previous, participant: Participant) => {
        if (participant === this.room?.localParticipant) this.callbacks.onLocalPermissionsChanged?.();
      })
      .on(RoomEvent.ParticipantConnected, (p: RemoteParticipant) => {
        this.callbacks.onParticipantConnected?.(p);
      })
      .on(RoomEvent.ParticipantDisconnected, (p: RemoteParticipant) => {
        this.quarantinedParticipants.delete(p.identity);
        this.callbacks.onParticipantDisconnected?.(p);
      })
      .on(RoomEvent.TrackSubscribed, (track: RemoteTrack, pub: RemoteTrackPublication, p: RemoteParticipant) => {
        if (this.encryptionRequired && this.quarantinedParticipants.has(p.identity)) {
          this.rejectRemoteTrack(pub);
          return;
        }
        if (this.encryptionRequired && !pub.isEncrypted) {
          this.rejectRemoteTrack(pub);
          return;
        }
        this.callbacks.onTrackSubscribed?.(track, pub, p);
      })
      .on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack, pub: RemoteTrackPublication, p: RemoteParticipant) => {
        this.callbacks.onTrackUnsubscribed?.(track, pub, p);
      })
      .on(RoomEvent.TrackMuted, (pub: TrackPublication, p: Participant) => {
        this.callbacks.onTrackMuted?.(pub, p);
      })
      .on(RoomEvent.TrackUnmuted, (pub: TrackPublication, p: Participant) => {
        this.callbacks.onTrackUnmuted?.(pub, p);
      })
      .on(RoomEvent.ActiveSpeakersChanged, (speakers: Participant[]) => {
        this.callbacks.onActiveSpeakersChanged?.(speakers);
      })
      .on(RoomEvent.ConnectionQualityChanged, (q: ConnectionQuality, p: Participant) => {
        this.callbacks.onConnectionQualityChanged?.(q, p);
      })
      .on(RoomEvent.TrackPublished, (pub: RemoteTrackPublication, participant: RemoteParticipant) => {
        if (this.encryptionRequired && this.quarantinedParticipants.has(participant.identity)) {
          this.rejectRemoteTrack(pub);
          return;
        }
        if (this.encryptionRequired && !pub.isEncrypted) {
          this.rejectRemoteTrack(pub);
          return;
        }
        console.info('[LiveKitClient] TrackPublished:', pub.kind, pub.source, 'from', participant.identity);
        if (!pub.isSubscribed) {
          console.info(`[LiveKitClient] Automatically subscribing to ${pub.kind} (${pub.source}) from ${participant.identity}`);
          pub.setSubscribed(true);
        }
        this.callbacks.onTrackPublished?.(pub, participant);
      })
      .on(RoomEvent.TrackUnpublished, (pub: RemoteTrackPublication, participant: RemoteParticipant) => {
        console.info('[LiveKitClient] TrackUnpublished:', pub.kind, pub.source, 'from', participant.identity);
        this.callbacks.onTrackUnpublished?.(pub, participant);
      })
      .on(RoomEvent.LocalTrackPublished, (pub: LocalTrackPublication, participant: LocalParticipant) => {
        if (this.encryptionRequired && !pub.isEncrypted) {
          this.rejectUnencryptedTrack();
          return;
        }
        console.info('[LiveKitClient] LocalTrackPublished:', pub.kind, pub.source);
        this.callbacks.onLocalTrackPublished?.(pub, participant);
      })
      .on(RoomEvent.LocalTrackUnpublished, (pub: LocalTrackPublication, participant: LocalParticipant) => {
        console.info('[LiveKitClient] LocalTrackUnpublished:', pub.kind, pub.source);
        this.callbacks.onLocalTrackUnpublished?.(pub, participant);
      })
      .on(RoomEvent.Reconnecting, () => {
        console.warn('[LiveKitClient] Reconnecting...');
        this.callbacks.onReconnecting?.();
      })
      .on(RoomEvent.Reconnected, () => {
        console.info('[LiveKitClient] Reconnected successfully - resyncing all tracks');
        this.reattachAllRemoteTracks();
        this.callbacks.onReconnected?.();
      })
      .on(RoomEvent.Disconnected, (reason?: DisconnectReason) => {
        this.callbacks.onDisconnected?.(reason);
      })
      .on(RoomEvent.MediaDevicesError, (err: Error) => {
        console.error('[LiveKitClient] MediaDevicesError:', err.message);
      })
      .on(RoomEvent.SignalConnected, () => {
        console.info('[LiveKitClient] Signal connected');
      })
      .on(RoomEvent.AudioPlaybackStatusChanged, () => {
        this.callbacks.onAudioPlaybackChanged?.(this.room?.canPlaybackAudio ?? true);
      })
      .on(RoomEvent.DataReceived, async (payload: Uint8Array, participant) => {
        if (this.encryptionRequired) {
          try {
            if (!this.conferenceDataKey) throw new Error('Cheie indisponibilă.');
            payload = await decryptConferenceData(this.conferenceDataKey, payload);
          } catch (_) {
            // Never parse or deliver unauthenticated data, but do not let a
            // single invalid packet disconnect everyone else in the room.
            const now = Date.now();
            if (now - this.lastRemoteEncryptionWarning > 10000) {
              this.lastRemoteEncryptionWarning = now;
              this.callbacks.onRemoteEncryptionProblem?.();
            }
            return;
          }
        }
        try {
          const str = new TextDecoder().decode(payload);
          const data = JSON.parse(str);
          if (data.type === 'session_history' && participant && Array.isArray(data.messages)) {
            this.callbacks.onSessionHistory?.(data.messages);
          } else if (data.type === 'chat') {
            this.callbacks.onChatMessage?.({
              id: data.id,
              senderName: data.senderName || participant?.name || 'Participant',
              senderIdentity: participant?.identity || data.senderIdentity || '',
              text: data.text,
              timestamp: data.timestamp,
              isSelf: false,
              replyTo: data.replyTo && typeof data.replyTo.id === 'string' && typeof data.replyTo.senderName === 'string' && typeof data.replyTo.text === 'string' ? { id: data.replyTo.id.slice(0,100), senderName: data.replyTo.senderName.slice(0,160), text: data.replyTo.text.slice(0,160) } : undefined,
            });
          } else if (data.type === 'chat_reaction' && participant && typeof data.messageId === 'string' && ['👍','❤️','👏','😂'].includes(data.emoji) && typeof data.active === 'boolean') {
            this.callbacks.onChatReaction?.({messageId:data.messageId.slice(0,100),emoji:data.emoji,active:data.active,identity:participant.identity});
          } else if (data.type === 'moderation') {
            this.callbacks.onModerationMessage?.({
              action: data.action,
              targetIdentity: data.targetIdentity,
              targetName: data.targetName,
              senderIdentity: participant?.identity || data.senderIdentity || '',
              newName: data.newName,
            });
          } else if (data.type === 'hand_raise') {
            this.callbacks.onHandRaise?.({
              identity: data.identity || participant?.identity || '',
              isRaised: !!data.isRaised,
              participantName: data.name || participant?.name || 'Participant',
            });
          } else if (data.type === 'reaction') {
            this.callbacks.onReaction?.({
              identity: data.identity || participant?.identity || '',
              emoji: data.emoji || '👍',
              participantName: data.name || participant?.name || 'Participant',
            });
          } else if (data.type === 'screen_share_status') {
            this.callbacks.onScreenShareStatus?.({
              isSharing: !!data.isSharing,
              identity: data.identity || participant?.identity || '',
              sharerName: data.sharerName || participant?.name || 'Participant',
            });
          } else if (data.type === 'recording_status') {
            this.callbacks.onRecordingStatus?.({
              isRecording: !!data.isRecording,
              recorderName: data.recorderName || participant?.name || 'Gazda',
              identity: data.identity || participant?.identity || '',
              recordingSeconds: typeof data.recordingSeconds === 'number' ? data.recordingSeconds : undefined,
            });
          } else if (data.type === 'query_recording_status') {
            this.callbacks.onQueryRecordingStatus?.({
              fromIdentity: data.identity || participant?.identity || '',
              fromName: participant?.name || '',
            });
          } else if (data.type === 'recording_command') {
            this.callbacks.onRecordingCommand?.({
              action: data.action || 'stop',
              requestedBy: data.requestedBy || participant?.name || 'Administrator',
            });
          } else if (data.type === 'whiteboard_draw') {
            this.callbacks.onWhiteboardDraw?.({
              x0: data.x0,
              y0: data.y0,
              x1: data.x1,
              y1: data.y1,
              color: data.color || '#38bdf8',
              size: data.size || 3,
              tool: data.tool || 'pen',
            });
          } else if (data.type === 'whiteboard_clear') {
            this.callbacks.onWhiteboardClear?.();
          } else if (data.type === 'whiteboard_toggle') {
            this.callbacks.onWhiteboardToggle?.({
              isOpen: !!data.isOpen,
              senderName: data.senderName || participant?.name || 'Participant',
            });
          } else if (data.type === 'poll_launch' && participant && /^host-[a-f0-9]{32}$/.test(participant.identity)) {
            this.callbacks.onPollLaunch?.({
              pollId: data.pollId,
              question: data.question,
              options: data.options || [],
              creatorName: participant.name || 'Gazda',
              creatorIdentity: participant.identity,
            });
          } else if (data.type === 'poll_vote' && participant) {
            this.callbacks.onPollVote?.({
              pollId: data.pollId,
              optionIndex: Number(data.optionIndex),
              voterName: data.voterName || participant?.name || 'Participant',
              voterIdentity: participant.identity,
            });
          } else if (data.type === 'poll_close' && participant && /^host-[a-f0-9]{32}$/.test(participant.identity)) {
            this.callbacks.onPollClose?.({
              pollId: data.pollId,
              senderIdentity: participant.identity,
            });
          } else if (data.type === 'chat_file') {
            const chatMsg: ChatMessage = {
              id: data.id,
              senderName: data.senderName || participant?.name || 'Participant',
              senderIdentity: participant?.identity || data.senderIdentity || '',
              text: data.text || '',
              timestamp: data.timestamp || new Date().toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }),
              isSelf: false,
              file: data.file,
            };
            this.callbacks.onChatMessage?.(chatMsg);
            this.callbacks.onChatFile?.(chatMsg);
          } else if (data.type === 'chat_file_chunk') {
            this.handleIncomingFileChunk(data, participant);
          } else if (data.type === 'waiting_room_knock') {
            this.callbacks.onWaitingRoomKnock?.({
              identity: data.identity || participant?.identity || '',
              name: data.name || participant?.name || 'Participant',
              timestamp: data.timestamp || Date.now(),
            });
          } else if (data.type === 'waiting_room_decision') {
            this.callbacks.onWaitingRoomDecision?.({
              targetIdentity: data.targetIdentity,
              decision: data.decision,
            });
          } else if (data.type === 'waiting_room_move_back') {
            this.callbacks.onWaitingRoomMoveBack?.({
              targetIdentity: data.targetIdentity,
            });
          } else if (data.type === 'host_status') {
            this.callbacks.onHostStatus?.({
              hostConnected: !!data.hostConnected,
              hostName: data.hostName || participant?.name || 'Gazda',
            });
          } else if (data.type === 'meeting_ended_by_host' || data.type === 'room_closed') {
            this.callbacks.onMeetingEndedByHost?.({
              hostName: data.hostName || participant?.name || 'Gazda',
              reason: data.reason || 'Conferința a fost încheiată de către gazdă.',
            });
          } else if (data.type === 'admin_layout_sync') {
            this.callbacks.onAdminLayoutSync?.({
              viewMode: data.viewMode,
              pinnedIdentity: data.pinnedIdentity,
              pinnedName: data.pinnedName,
              adminName: data.adminName,
              isStageLocked: Boolean(data.isStageLocked),
              senderIdentity: data.senderIdentity || participant?.identity,
              isPrimaryHost: Boolean(data.isPrimaryHost),
            });
          }
        } catch (e) {
          console.error('[LiveKitClient] Failed to parse in-room data message', e);
        }
      });
  }

  public setInitialCameraStream(stream: MediaStream | null): void {
    if (stream && stream.active) {
      this.localCameraStream = stream;
      this.isCameraEnabled = true;
      console.info('[LiveKitClient] Transferred active camera stream seamlessly from pre-join');
    }
  }

  public async enableCamera(enable: boolean, quality: 'hd' | 'fullhd' = 'hd'): Promise<boolean> {
    this.isCameraEnabled = enable;
    const camPreset = quality === 'fullhd' ? VideoPresets.h1080 : VideoPresets.h720;

    if (!this.room || this.room.state !== 'connected') {
      if (!enable) {
        if (this.localCameraStream) {
          try {
            this.localCameraStream.getTracks().forEach(t => t.stop());
          } catch (_) {}
          this.localCameraStream = null;
        }
        return true;
      }

      // Pre-connection or offline fallback: acquire local camera immediately so user NEVER sees a black screen!
      if (!this.localCameraStream || !this.localCameraStream.active) {
        try {
          const preferredCamId = typeof localStorage !== 'undefined' ? localStorage.getItem('vorbello_selected_cam') : null;
          this.localCameraStream = await acquireUserCameraStream(preferredCamId, quality);
          console.info(`[LiveKitClient] Early local camera stream acquired successfully (${quality.toUpperCase()})`);
        } catch (e) {
          console.warn('[LiveKitClient] Failed to acquire early local camera stream:', e);
        }
      }
      return true;
    }

    const pub = this.room.localParticipant.getTrackPublication(Track.Source.Camera);

    // MUTE CAMERA (false):
    if (!enable) {
      if (pub) {
        try {
          if (pub.track) {
            pub.track.mediaStreamTrack.enabled = false;
          }
          await pub.mute();
          console.info('[LiveKitClient] Camera muted instantly (track kept alive)');
        } catch (e) {
          console.warn('[LiveKitClient] Error muting camera track:', e);
        }
      }
      try {
        await this.room.localParticipant.setCameraEnabled(false);
      } catch (_) {}
      return true;
    }

    // ENABLE CAMERA (true):
    // 1. Fast path: if track was already published, simply enable track and unmute! (0ms delay)
    if (pub && pub.track) {
      const msTrack = pub.track.mediaStreamTrack;
      if (msTrack && msTrack.readyState === 'live') {
        try {
          msTrack.enabled = true;
          await pub.unmute();
          console.info('[LiveKitClient] Camera unmuted instantly (fast path, 0ms)');
          return true;
        } catch (e) {
          console.warn('[LiveKitClient] Fast unmute error, will re-publish:', e);
        }
      }
    }

    // Seamless camera transition: If we already acquired a live camera track during pre-join,
    // publish it directly to LiveKit without turning off the camera hardware! (0ms flash)
    if (this.localCameraStream) {
      const existingTrack = this.localCameraStream.getVideoTracks()[0];
      if (existingTrack && existingTrack.readyState === 'live') {
        try {
          existingTrack.enabled = true;
          this.cachedCameraMst = existingTrack;
          this.cachedCameraStream = this.localCameraStream;
          this.isCameraEnabled = true;
          const pub = await this.room.localParticipant.publishTrack(existingTrack, {
            name: 'camera',
            source: Track.Source.Camera,
            simulcast: false,
            videoEncoding: camPreset.encoding,
          });
          console.info(`[LiveKitClient] Camera published seamlessly from active track (${quality.toUpperCase()}, 0ms flash):`, pub?.trackSid);
          return true;
        } catch (pubErr) {
          console.warn('[LiveKitClient] publishTrack failed for existing camera, falling back to setCameraEnabled:', pubErr);
          try {
            this.localCameraStream.getTracks().forEach(t => t.stop());
          } catch (_) {}
          this.localCameraStream = null;
          await new Promise(r => setTimeout(r, 60));
        }
      } else {
        try {
          this.localCameraStream.getTracks().forEach(t => t.stop());
        } catch (_) {}
        this.localCameraStream = null;
      }
    }

    // Publish camera via LiveKit native setCameraEnabled
    let camOptions: any = undefined;
    const isMobile = typeof navigator !== 'undefined' && (
      /android|iphone|ipad|ipod/i.test(navigator.userAgent) ||
      (navigator.maxTouchPoints && navigator.maxTouchPoints > 1)
    );

    if (isMobile) {
      camOptions = {
        facingMode: this.currentFacingMode || 'user',
        resolution: camPreset.resolution,
      };
      console.info(`[LiveKitClient] Mobile camera enable with facingMode: ${this.currentFacingMode || 'user'} (${quality.toUpperCase()})`);
    } else {
      let preferredCamId = typeof localStorage !== 'undefined' ? localStorage.getItem('vorbello_selected_cam') : null;
      camOptions = {
        deviceId: preferredCamId && preferredCamId !== 'default' && preferredCamId.trim() !== '' ? preferredCamId : undefined,
        resolution: camPreset.resolution,
      };
    }

    try {
      if (camOptions) {
        await this.room.localParticipant.setCameraEnabled(true, camOptions);
      } else {
        await this.room.localParticipant.setCameraEnabled(true);
      }
      this.isCameraEnabled = true;
      console.info(`[LiveKitClient] Camera published to room successfully (${quality.toUpperCase()})`);
      return true;
    } catch (err) {
      console.warn('[LiveKitClient] setCameraEnabled with deviceId failed, retrying universal default:', err);
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('vorbello_selected_cam');
      }
      try {
        await this.room.localParticipant.setCameraEnabled(true);
        this.isCameraEnabled = true;
        console.info('[LiveKitClient] Camera published via default device fallback');
        return true;
      } catch (retryErr) {
        console.error('[LiveKitClient] Failed to publish camera to room:', retryErr);
        this.isCameraEnabled = false;
        return false;
      }
    }
  }

  public async flipCamera(): Promise<boolean> {
    if (!this.room) return false;

    // Case 1: Room connected: switch active device directly (v1.4.5 proven reliable method)
    if (this.room.state === 'connected') {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevs = devices.filter(d => d.kind === 'videoinput');
        if (videoDevs.length < 2) {
          console.warn('[LiveKitClient] Less than 2 video devices available, cannot flip');
          return false;
        }

        const pub = this.room.localParticipant.getTrackPublication(Track.Source.Camera);
        const activeTrackDeviceId = pub?.track?.mediaStreamTrack?.getSettings()?.deviceId;
        let currentId = activeTrackDeviceId || (typeof localStorage !== 'undefined' ? localStorage.getItem('vorbello_selected_cam') : null);

        let nextIndex = 0;
        if (currentId) {
          const currIdx = videoDevs.findIndex(d => d.deviceId === currentId);
          if (currIdx !== -1) {
            nextIndex = (currIdx + 1) % videoDevs.length;
          }
        }

        const nextDev = videoDevs[nextIndex];
        if (nextDev) {
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem('vorbello_selected_cam', nextDev.deviceId);
          }
          const label = (nextDev.label || '').toLowerCase();
          if (label.includes('front') || label.includes('user') || label.includes('selfie') || label.includes('anterior') || label.includes('față')) {
            this.currentFacingMode = 'user';
          } else if (label.includes('back') || label.includes('rear') || label.includes('environment') || label.includes('spate') || label.includes('posterior')) {
            this.currentFacingMode = 'environment';
          } else {
            this.currentFacingMode = this.currentFacingMode === 'user' ? 'environment' : 'user';
          }

          // Native LiveKit device switch without unpublishing/killing the WebRTC track
          await this.room.switchActiveDevice('videoinput', nextDev.deviceId, true);
          this.isCameraEnabled = true;
          console.info('[LiveKitClient] Switched active camera seamlessly to:', nextDev.label || nextDev.deviceId);
          return true;
        }
      } catch (e) {
        console.warn('[LiveKitClient] flipCamera room switch error:', e);
      }
      return false;
    }

    // Case 2: Pre-join or preview mode
    try {
      this.currentFacingMode = this.currentFacingMode === 'user' ? 'environment' : 'user';
      if (this.localCameraStream) {
        try {
          this.localCameraStream.getTracks().forEach(t => t.stop());
        } catch (_) {}
        this.localCameraStream = null;
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: this.currentFacingMode } },
        audio: false,
      });
      this.localCameraStream = stream;
      this.isCameraEnabled = true;
      return true;
    } catch (e) {
      console.warn('[LiveKitClient] flipCamera preview error:', e);
      return false;
    }
  }

  public async enableMicrophone(enable: boolean): Promise<boolean> {
    this.isMicEnabled = enable;

    if (!this.room || this.room.state !== 'connected') {
      return true;
    }

    const pub = this.room.localParticipant.getTrackPublication(Track.Source.Microphone);

    if (!enable) {
      if (pub) {
        try {
          if (pub.track) {
            pub.track.mediaStreamTrack.enabled = false;
          }
          await pub.mute();
          console.info('[LiveKitClient] Microphone muted instantly');
        } catch (e) {
          console.warn('[LiveKitClient] Error muting mic track:', e);
        }
      }
      try {
        await this.room.localParticipant.setMicrophoneEnabled(false);
      } catch (_) {}
      return true;
    }

    // ENABLE MICROPHONE (true):
    // 1. Fast path: if track was already published and still live, simply enable track and unmute! (Takes 0ms)
    if (pub && pub.track) {
      const msTrack = pub.track.mediaStreamTrack;
      if (msTrack && msTrack.readyState === 'live') {
        try {
          msTrack.enabled = true;
          await pub.unmute();
          console.info('[LiveKitClient] Microphone unmuted instantly (fast path)');
          return true;
        } catch (e) {
          console.warn('[LiveKitClient] Fast mic unmute error, fallback to setMicrophoneEnabled:', e);
        }
      }
    }

    // 2. Initial publication: select device without pre-locking hardware
    let micOpts: any = undefined;
    let preferredMicId = typeof localStorage !== 'undefined' ? localStorage.getItem('vorbello_selected_mic') : null;

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioDevs = devices.filter(d => d.kind === 'audioinput');
      let selected = audioDevs.find(d => d.deviceId === preferredMicId);
      if (!selected) {
        // Prioritize Yeti Nano or dedicated microphone
        const bestMic = audioDevs.find(d => {
          const l = (d.label || '').toLowerCase();
          return l.includes('yeti') || l.includes('nano');
        }) || audioDevs.find(d => {
          const l = (d.label || '').toLowerCase();
          return l.includes('micro') || l.includes('angetube');
        }) || audioDevs[0];
        if (bestMic) {
          preferredMicId = bestMic.deviceId;
          localStorage.setItem('vorbello_selected_mic', preferredMicId);
        }
      }
      if (preferredMicId && preferredMicId !== 'default') {
        micOpts = { deviceId: preferredMicId };
      }
    } catch (_) {}

    try {
      await this.room.localParticipant.setMicrophoneEnabled(true, micOpts);
      console.info('[LiveKitClient] Microphone published successfully');
      return true;
    } catch (err) {
      console.warn('[LiveKitClient] setMicrophoneEnabled error, retrying default:', err);
      try {
        await this.room.localParticipant.setMicrophoneEnabled(true);
        return true;
      } catch (retryErr) {
        console.error('[LiveKitClient] Failed to publish microphone to room:', retryErr);
        return false;
      }
    }
  }

  public async enableScreenShare(
    enable: boolean,
    withAudio: boolean = false,
    quality: 'hd' | 'fullhd' = 'hd'
  ): Promise<boolean> {
    if (enable && this.screenStopTask) {
      console.warn('[LiveKitClient] Previous screen share is still stopping');
      return false;
    }
    this.isScreenShareEnabled = enable;

    if (!enable) {
      // Disarm all onended handlers BEFORE stopping tracks to prevent re-entrant loops
      if (this.cachedScreenMst) {
        this.cachedScreenMst.onended = null;
        try { this.cachedScreenMst.stop(); } catch (_) {}
      }
      if (this.cachedScreenStream) {
        try {
          this.cachedScreenStream.getTracks().forEach(t => {
            t.onended = null;
            t.stop();
          });
        } catch (_) {}
      }
      if (this.localScreenStream) {
        try {
          this.localScreenStream.getTracks().forEach(t => {
            t.onended = null;
            t.stop();
          });
        } catch (_) {}
      }
      this.cachedScreenStream = null;
      this.cachedScreenMst = null;
      this.localScreenStream = null;

      this.callbacks.onLocalScreenShareEnded?.();
      if (this.room && this.room.state === 'connected' && !this.screenStopTask) {
        const stoppingRoom = this.room;
        const task = (async () => {
        try {
          const screenPubs = Array.from(stoppingRoom.localParticipant.trackPublications.values())
            .filter(p => p.source === Track.Source.ScreenShare || p.source === Track.Source.ScreenShareAudio);
          for (const sp of screenPubs) {
            if (sp.track) {
              try {
                await stoppingRoom.localParticipant.unpublishTrack(sp.track, true);
                console.info('[LiveKitClient] Explicitly unpublished screen track:', sp.trackSid);
              } catch (unpubErr) {
                console.warn('[LiveKitClient] unpublishTrack error:', unpubErr);
              }
            }
          }
          try {
            await stoppingRoom.localParticipant.setScreenShareEnabled(false);
          } catch (_) {}
          console.info('[LiveKitClient] Screen share disabled successfully');
        } catch (err) {
          console.warn('[LiveKitClient] Error disabling screen share:', err);
        }

        try {
          if (this.room === stoppingRoom) await this.sendDataMessage({
            type: 'screen_share_status',
            isSharing: false,
            identity: stoppingRoom.localParticipant.identity,
          });
        } catch (_) {}
        })();
        this.screenStopTask = task;
        void task.finally(() => { if (this.screenStopTask === task) this.screenStopTask = null; });
      }
      return true;
    }

    // ENABLE SCREEN SHARE:
    if (this.room && this.room.state === 'connected') {
      let stream: MediaStream | null = null;
      try {
        const isFhd = quality === 'fullhd';
        const targetWidth = isFhd ? 1920 : 1280;
        const targetHeight = isFhd ? 1080 : 720;
        const opts: any = {
          video: {
            width: { ideal: targetWidth, max: targetWidth },
            height: { ideal: targetHeight, max: targetHeight },
            frameRate: { ideal: 30, max: 30 },
          },
          audio: withAudio ? { restrictOwnAudio: true } : false,
          selfBrowserSurface: 'exclude',
          surfaceSwitching: 'include',
          systemAudio: withAudio ? 'include' : 'exclude',
        };
        try {
          stream = await captureDisplayMedia(opts);
        } catch (gdmErr: any) {
          if (gdmErr && (gdmErr.name === 'NotAllowedError' || gdmErr.name === 'AbortError')) throw gdmErr;
          console.warn('[LiveKitClient] getDisplayMedia with rich options failed, retrying with simple constraints:', gdmErr);
          const simpleOpts: any = { video: true, audio: withAudio ? { restrictOwnAudio: true } : false };
          try {
            stream = await captureDisplayMedia(simpleOpts);
          } catch (gdmErr2: any) {
            if (gdmErr2 && (gdmErr2.name === 'NotAllowedError' || gdmErr2.name === 'AbortError')) throw gdmErr2;
            const noAudioOpts: any = { video: true, audio: false };
            stream = await captureDisplayMedia(noAudioOpts);
          }
        }

        const mst = stream.getVideoTracks()[0];
        if (!mst) throw new Error('No video track in display stream');
        try { (mst as any).contentHint = 'detail'; } catch (_) {}

        this.cachedScreenMst = mst;
        this.cachedScreenStream = stream;
        this.localScreenStream = stream;
        this.isScreenShareEnabled = true;

        // CRITICAL: Notify UI IMMEDIATELY with local stream so stage renders at 0 ms without waiting for SFU server negotiation!
        this.callbacks.onLocalScreenStreamReady?.(stream);

        mst.onended = async () => {
          console.info('[LiveKitClient] Screen share track ended by user');
          await this.enableScreenShare(false);
        };

        // Clean any leftover screen track publications to avoid duplicate publication errors
        const oldScreenPub = this.room.localParticipant.getTrackPublication(Track.Source.ScreenShare);
        if (oldScreenPub && oldScreenPub.track) {
          try { await this.room.localParticipant.unpublishTrack(oldScreenPub.track, true); } catch (_) {}
        }
        const oldAudioPub = this.room.localParticipant.getTrackPublication(Track.Source.ScreenShareAudio);
        if (oldAudioPub && oldAudioPub.track) {
          try { await this.room.localParticipant.unpublishTrack(oldAudioPub.track, true); } catch (_) {}
        }

        // 2. Publish video track directly to LiveKit server with selected quality preset (HD or Full HD @ 30fps)
        const pub = await this.room.localParticipant.publishTrack(mst, {
          name: 'screen_share',
          source: Track.Source.ScreenShare,
          simulcast: false,
          videoCodec: this.screenVideoCodec(),
          videoEncoding: (isFhd ? ScreenSharePresets.h1080fps30 : ScreenSharePresets.h720fps30).encoding,
          degradationPreference: 'maintain-resolution',
        });
        console.info(`[LiveKitClient] Screen share video published (${quality.toUpperCase()} @ 30 FPS):`, pub?.trackSid);

        // 3. Publish screen audio track if present (tab audio, system audio, video player audio)
        const audioTracks = stream.getAudioTracks();
        if (audioTracks.length > 0) {
          const screenAudioTrack = audioTracks[0];
          try {
            const audioPub = await this.room.localParticipant.publishTrack(screenAudioTrack, {
              name: 'screen_share_audio',
              source: Track.Source.ScreenShareAudio,
              dtx: false,
            });
            console.info('[LiveKitClient] Screen share audio published successfully:', audioPub?.trackSid);
          } catch (audioPubErr) {
            console.warn('[LiveKitClient] Failed to publish screen share audio:', audioPubErr);
          }
        } else {
          console.info('[LiveKitClient] No screen audio track provided (user might not have checked "Share audio" in browser dialog)');
        }

        await this.sendDataMessage({
          type: 'screen_share_status',
          isSharing: true,
          identity: this.room.localParticipant.identity,
          sharerName: this.displayName || this.room.localParticipant.name || 'Participant',
        });
        return true;
      } catch (err: any) {
        console.warn('[LiveKitClient] Screen share error:', err);
        try {
          stream?.getTracks().forEach(t => {
            t.onended = null;
            t.stop();
          });
        } catch (_) {}
        this.isScreenShareEnabled = false;
        this.localScreenStream = null;
        this.cachedScreenStream = null;
        this.cachedScreenMst = null;
        return false;
      }
    } else {
      // Offline / Preview fallback
      if (typeof navigator !== 'undefined' && navigator.mediaDevices && typeof navigator.mediaDevices.getDisplayMedia === 'function') {
        try {
          let stream: MediaStream;
          try {
            stream = await navigator.mediaDevices.getDisplayMedia({
              video: true,
              audio: { echoCancellation: true },
            });
          } catch (_) {
            stream = await navigator.mediaDevices.getDisplayMedia({
              video: true,
              audio: false,
            });
          }
          this.localScreenStream = stream;
          this.cachedScreenStream = stream;
          const tr = stream.getVideoTracks()[0];
          if (tr) {
            this.cachedScreenMst = tr;
            tr.onended = async () => {
              await this.enableScreenShare(false);
            };
          }
          this.isScreenShareEnabled = true;
          this.callbacks.onLocalScreenStreamReady?.(stream);
          return true;
        } catch (e) {
          this.isScreenShareEnabled = false;
          return false;
        }
      }
    }
    return false;
  }

  public setLocalScreenStream(stream: MediaStream | null): void {
    this.localScreenStream = stream;
    this.cachedScreenStream = stream;
    if (stream) {
      this.cachedScreenMst = stream.getVideoTracks()[0] || null;
      this.isScreenShareEnabled = true;
      this.callbacks.onLocalScreenStreamReady?.(stream);
    }
  }

  public async replaceScreenShareTrack(
    newTrack: MediaStreamTrack,
    newAudioTrack?: MediaStreamTrack | null,
    quality: 'hd' | 'fullhd' = 'hd'
  ): Promise<boolean> {
    const previousStream = this.localScreenStream;
    try {
      const tracksForStream = [newTrack];
      if (newAudioTrack) tracksForStream.push(newAudioTrack);
      const newStream = new MediaStream(tracksForStream);

      const isFhd = quality === 'fullhd';
      const targetPreset = isFhd ? ScreenSharePresets.h1080fps30 : ScreenSharePresets.h720fps30;
      try { newTrack.contentHint = 'detail'; } catch (_) {}

      if (this.room && this.room.state === 'connected') {
        const pub = this.room.localParticipant.getTrackPublication(Track.Source.ScreenShare);
        if (pub && pub.track) {
          try {
            if (typeof (pub.track as any).replaceTrack === 'function') {
              await (pub.track as any).replaceTrack(newTrack, true);
            } else if ((pub.track as any).sender && typeof (pub.track as any).sender.replaceTrack === 'function') {
              await (pub.track as any).sender.replaceTrack(newTrack);
              (pub.track as any).mediaStreamTrack = newTrack;
              (pub.track as any)._mediaStreamTrack = newTrack;
            } else {
              await this.room.localParticipant.unpublishTrack(pub.track);
              await this.room.localParticipant.publishTrack(newTrack, {
                name: 'screen_share',
                source: Track.Source.ScreenShare,
                simulcast: false,
                videoCodec: this.screenVideoCodec(),
                videoEncoding: targetPreset.encoding,
                degradationPreference: 'maintain-resolution',
              });
            }
          } catch (replaceErr) {
            console.warn('[LiveKitClient] replaceTrack error, falling back to republish:', replaceErr);
            try {
              if (pub && pub.track) await this.room.localParticipant.unpublishTrack(pub.track);
            } catch (_) {}
            await this.room.localParticipant.publishTrack(newTrack, {
              name: 'screen_share',
              source: Track.Source.ScreenShare,
              simulcast: false,
              videoCodec: this.screenVideoCodec(),
              videoEncoding: targetPreset.encoding,
              degradationPreference: 'maintain-resolution',
            });
          }
        } else {
          await this.room.localParticipant.publishTrack(newTrack, {
            name: 'screen_share',
            source: Track.Source.ScreenShare,
            simulcast: false,
            videoCodec: this.screenVideoCodec(),
            videoEncoding: targetPreset.encoding,
            degradationPreference: 'maintain-resolution',
          });
        }

        // 4. Update or publish screen audio track publication
        const audioPub = this.room.localParticipant.getTrackPublication(Track.Source.ScreenShareAudio);
        if (newAudioTrack) {
          if (audioPub && audioPub.track) {
            try {
              if (typeof (audioPub.track as any).replaceTrack === 'function') {
                await (audioPub.track as any).replaceTrack(newAudioTrack, true);
              } else {
                try { (audioPub.track as any).mediaStreamTrack?.stop(); audioPub.track.stop(); } catch (_) {}
                await this.room.localParticipant.unpublishTrack(audioPub.track, true);
                await this.room.localParticipant.publishTrack(newAudioTrack, {
                  name: 'screen_share_audio',
                  source: Track.Source.ScreenShareAudio,
                  dtx: false,
                });
              }
            } catch (_) {
              try {
                (audioPub.track as any).mediaStreamTrack?.stop();
                audioPub.track.stop();
                await this.room.localParticipant.unpublishTrack(audioPub.track, true);
              } catch (_) {}
              await this.room.localParticipant.publishTrack(newAudioTrack, {
                name: 'screen_share_audio',
                source: Track.Source.ScreenShareAudio,
                dtx: false,
              });
            }
          } else {
            try {
              await this.room.localParticipant.publishTrack(newAudioTrack, {
                name: 'screen_share_audio',
                source: Track.Source.ScreenShareAudio,
                dtx: false,
              });
            } catch (aErr) {
              console.warn('[LiveKitClient] Failed to publish screen audio track on swap:', aErr);
            }
          }
        } else {
          // If the new source does not have audio, explicitly STOP and unpublish any previous screen audio track
          if (audioPub && audioPub.track) {
            try {
              console.info('[LiveKitClient] Stopping and unpublishing old screen audio track on swap to silent source...');
              try { (audioPub.track as any).mediaStreamTrack?.stop(); } catch (_) {}
              try { audioPub.track.stop(); } catch (_) {}
              await this.room.localParticipant.unpublishTrack(audioPub.track, true);
            } catch (_) {}
          }
        }

        try {
          await this.sendDataMessage({
            type: 'screen_share_status',
            isSharing: true,
            identity: this.room.localParticipant.identity,
            sharerName: this.displayName || this.room.localParticipant.name || 'Participant',
          });
        } catch (_) {}

        try {
          await this.sendDataMessage({
            type: 'screen_audio_status',
            hasAudio: Boolean(newAudioTrack),
            identity: this.room.localParticipant.identity,
          });
        } catch (_) {}
      }

      newTrack.onended = async () => {
        console.info('[LiveKitClient] Screen share track ended by user');
        await this.enableScreenShare(false);
      };

      this.cachedScreenMst = newTrack;
      this.cachedScreenStream = newStream;
      this.localScreenStream = newStream;
      this.isScreenShareEnabled = true;
      this.callbacks.onLocalScreenStreamReady?.(newStream);

      // Clean up previous tracks ONLY after the new track has been successfully installed
      previousStream?.getTracks().forEach(t => {
        if (t !== newTrack && t !== newAudioTrack) {
          t.onended = null;
          try { t.stop(); } catch (_) {}
        }
      });
      return true;
    } catch (err) {
      console.warn('[LiveKitClient] replaceScreenShareTrack fatal error:', err);
      newTrack.onended = null;
      try { newTrack.stop(); } catch (_) {}
      if (newAudioTrack) try { newAudioTrack.stop(); } catch (_) {}
      return false;
    }
  }

  public async sendDataMessage(data: any, destinationIdentity?: string): Promise<boolean> {
    if (this.encryptionRequired && !this.room?.isE2EEEnabled) return false;
    if (this.room && this.room.state === 'connected') {
      try {
        const payload = new TextEncoder().encode(JSON.stringify(data));
        await this.publishConferenceData(payload, destinationIdentity);
        return true;
      } catch (err) {
        console.warn('[LiveKitClient] Failed to broadcast data message:', err);
      }
    }
    return false;
  }

  private async publishConferenceData(payload: Uint8Array, destinationIdentity?: string): Promise<void> {
    if (!this.room || this.room.state !== 'connected') throw new Error('Conexiune indisponibilă.');
    if (this.encryptionRequired) {
      if (!this.conferenceDataKey || !this.room.isE2EEEnabled) throw new Error('Criptarea nu este disponibilă.');
      payload = await encryptConferenceData(this.conferenceDataKey, payload);
    }
    await this.room.localParticipant.publishData(new Uint8Array(payload), { reliable: true, ...(destinationIdentity ? { destinationIdentities: [destinationIdentity] } : {}) });
  }

  public async sendChatMessage(text: string, customSenderName?: string, replyTo?: ChatMessage['replyTo']): Promise<ChatMessage | null> {
    if (!text.trim()) return null;
    if (this.encryptionRequired && (!this.room || this.room.state !== 'connected' || !this.room.isE2EEEnabled)) return null;

    const outgoingName = customSenderName || this.displayName || this.room?.localParticipant?.name || 'Tu';
    const message: ChatMessage = {
      id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
      senderName: outgoingName,
      senderIdentity: this.room?.localParticipant?.identity || 'self',
      text: text.trim(),
      timestamp: new Date().toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }),
      isSelf: true,
      ...(replyTo ? { replyTo } : {}),
    };

    if (this.room && this.room.state === 'connected') {
      try {
        const payload = new TextEncoder().encode(JSON.stringify({
          type: 'chat',
          ...message,
          senderName: outgoingName === 'Tu' && this.displayName ? this.displayName : outgoingName,
        }));
        await this.publishConferenceData(payload);
      } catch (err) {
        console.warn('[LiveKitClient] Failed to broadcast chat message to remote participants:', err);
        if (this.encryptionRequired) return null;
      }
    }

    return message;
  }

  public async sendHandRaise(isRaised: boolean): Promise<boolean> {
    const identity = this.room?.localParticipant?.identity || 'self';
    const name = this.room?.localParticipant?.name || 'Tu';
    return this.sendDataMessage({
      type: 'hand_raise',
      identity,
      name,
      isRaised,
      timestamp: Date.now(),
    });
  }

  public async sendLowerAllHands(): Promise<boolean> {
    const name = this.room?.localParticipant?.name || this.displayName || 'Admin';
    return this.sendDataMessage({
      type: 'hand_raise',
      identity: 'all',
      isLowerAll: true,
      isRaised: false,
      name,
      timestamp: Date.now(),
    });
  }

  public async sendReaction(emoji: string): Promise<boolean> {
    const identity = this.room?.localParticipant?.identity || 'self';
    const name = this.room?.localParticipant?.name || 'Tu';
    return this.sendDataMessage({
      type: 'reaction',
      identity,
      name,
      emoji,
      timestamp: Date.now(),
    });
  }

  public async sendWhiteboardDraw(data: { x0: number; y0: number; x1: number; y1: number; color: string; size: number; tool: string }): Promise<boolean> {
    return this.sendDataMessage({
      type: 'whiteboard_draw',
      ...data,
      timestamp: Date.now(),
    });
  }

  public async sendWhiteboardClear(): Promise<boolean> {
    return this.sendDataMessage({
      type: 'whiteboard_clear',
      timestamp: Date.now(),
    });
  }

  public async sendWhiteboardToggle(isOpen: boolean): Promise<boolean> {
    const senderName = this.room?.localParticipant?.name || this.displayName || 'Gazda';
    return this.sendDataMessage({
      type: 'whiteboard_toggle',
      isOpen,
      senderName,
      timestamp: Date.now(),
    });
  }

  public async sendPollLaunch(pollId: string, question: string, options: string[]): Promise<boolean> {
    const creatorName = this.room?.localParticipant?.name || this.displayName || 'Gazda';
    return this.sendDataMessage({
      type: 'poll_launch',
      pollId,
      question,
      options,
      creatorName,
      timestamp: Date.now(),
    });
  }

  public async sendPollVote(pollId: string, optionIndex: number): Promise<boolean> {
    const voterIdentity = this.room?.localParticipant?.identity || 'self';
    const voterName = this.room?.localParticipant?.name || this.displayName || 'Participant';
    return this.sendDataMessage({
      type: 'poll_vote',
      pollId,
      optionIndex,
      voterName,
      voterIdentity,
      timestamp: Date.now(),
    });
  }

  public async sendPollClose(pollId: string): Promise<boolean> {
    return this.sendDataMessage({
      type: 'poll_close',
      pollId,
      timestamp: Date.now(),
    });
  }

  private handleIncomingFileChunk(data: any, participant?: RemoteParticipant): void {
    const transferId = data.transferId;
    if (!transferId || typeof data.chunkIndex !== 'number' || !data.totalChunks) return;

    let transfer = this.incomingFileTransfers.get(transferId);
    if (!transfer) {
      transfer = {
        chunks: new Array(data.totalChunks),
        receivedCount: 0,
        totalChunks: data.totalChunks,
        metadata: {
          name: data.name || 'document',
          size: data.size || 0,
          fileType: data.fileType || 'application/octet-stream',
          text: data.text || '',
          timestamp: data.timestamp || new Date().toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }),
          senderName: data.senderName || participant?.name || 'Participant',
          senderIdentity: participant?.identity || data.senderIdentity || '',
        },
        timeoutId: null,
      };
      this.incomingFileTransfers.set(transferId, transfer);
    }

    if (transfer.timeoutId) clearTimeout(transfer.timeoutId);
    transfer.timeoutId = setTimeout(() => {
      this.incomingFileTransfers.delete(transferId);
    }, 60000);

    if (!transfer.chunks[data.chunkIndex]) {
      transfer.chunks[data.chunkIndex] = data.data || '';
      transfer.receivedCount++;
    }

    const progress = Math.min(100, Math.round((transfer.receivedCount / transfer.totalChunks) * 100));

    this.callbacks.onFileTransferProgress?.({
      transferId,
      progress,
      name: transfer.metadata.name,
      size: transfer.metadata.size,
      senderName: transfer.metadata.senderName,
      senderIdentity: transfer.metadata.senderIdentity,
      timestamp: transfer.metadata.timestamp,
      text: transfer.metadata.text,
      isComplete: transfer.receivedCount === transfer.totalChunks,
    });

    if (transfer.receivedCount === transfer.totalChunks) {
      if (transfer.timeoutId) clearTimeout(transfer.timeoutId);
      const fullDataUrl = transfer.chunks.join('');
      this.incomingFileTransfers.delete(transferId);

      const chatMsg: ChatMessage = {
        id: transferId,
        senderName: transfer.metadata.senderName,
        senderIdentity: transfer.metadata.senderIdentity,
        text: transfer.metadata.text,
        timestamp: transfer.metadata.timestamp,
        isSelf: false,
        file: {
          name: transfer.metadata.name,
          size: transfer.metadata.size,
          type: transfer.metadata.fileType,
          dataUrl: fullDataUrl,
          progress: 100,
        },
      };

      this.callbacks.onChatMessage?.(chatMsg);
      this.callbacks.onChatFile?.(chatMsg);
    }
  }

  public async sendFileMessage(
    file: { name: string; size: number; type: string; dataUrl: string },
    text?: string,
    customSenderName?: string,
    onProgress?: (pct: number) => void
  ): Promise<ChatMessage | null> {
    if (this.encryptionRequired && (!this.room || this.room.state !== 'connected' || !this.room.isE2EEEnabled)) return null;
    const outgoingName = customSenderName || this.displayName || this.room?.localParticipant?.name || 'Tu';
    const messageId = `msg-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
    const message: ChatMessage = {
      id: messageId,
      senderName: outgoingName,
      senderIdentity: this.room?.localParticipant?.identity || 'self',
      text: (text || '').trim(),
      timestamp: new Date().toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }),
      isSelf: true,
      file,
    };

    if (this.room && this.room.state === 'connected') {
      try {
        const CHUNK_SIZE = 8192; // 8 KB safe chunk payload (guarantees packet stays below 15KB WebRTC limit with JSON metadata)
        const dataUrl = file.dataUrl || '';
        const totalChunks = Math.max(1, Math.ceil(dataUrl.length / CHUNK_SIZE));
        const senderName = outgoingName === 'Tu' && this.displayName ? this.displayName : outgoingName;
        const senderIdentity = this.room?.localParticipant?.identity || 'self';

        for (let i = 0; i < totalChunks; i++) {
          const chunkData = dataUrl.substring(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
          const chunkPayload = JSON.stringify({
            type: 'chat_file_chunk',
            transferId: messageId,
            chunkIndex: i,
            totalChunks,
            name: file.name,
            size: file.size,
            fileType: file.type,
            text: (text || '').trim(),
            timestamp: message.timestamp,
            senderName,
            senderIdentity,
            data: chunkData,
          });

          const encoded = new TextEncoder().encode(chunkPayload);
          await this.publishConferenceData(encoded);

          if (onProgress) {
            onProgress(Math.round(((i + 1) / totalChunks) * 100));
          }

          // Small pacing to avoid SCTP backpressure on large files
          if (i % 2 === 0 && i < totalChunks - 1) {
            await new Promise(r => setTimeout(r, 6));
          }
        }
      } catch (err) {
        console.warn('[LiveKitClient] Failed to broadcast chunked file message:', err);
        if (this.encryptionRequired) return null;
      }
    }

    return message;
  }

  public async sendWaitingRoomKnock(name: string): Promise<boolean> {
    const identity = this.room?.localParticipant?.identity || 'self';
    return this.sendDataMessage({
      type: 'waiting_room_knock',
      identity,
      name,
      timestamp: Date.now(),
    });
  }

  public async sendWaitingRoomDecision(targetIdentity: string, decision: 'admitted' | 'rejected'): Promise<boolean> {
    return this.sendDataMessage({
      type: 'waiting_room_decision',
      targetIdentity,
      decision,
      timestamp: Date.now(),
    });
  }

  public async sendWaitingRoomMoveBack(targetIdentity: string): Promise<boolean> {
    return this.sendDataMessage({
      type: 'waiting_room_move_back',
      targetIdentity,
      timestamp: Date.now(),
    });
  }

  /**
   * After a reconnection, force re-subscribe to all remote participant tracks.
   * This fixes the issue where tracks become detached/stale after network interruptions.
   */
  public reattachAllRemoteTracks(): void {
    if (!this.room || this.room.state !== 'connected') return;

    this.room.remoteParticipants.forEach((rp) => {
      rp.trackPublications.forEach((pub) => {
        if (this.encryptionRequired && (this.quarantinedParticipants.has(rp.identity) || !pub.isEncrypted)) return;
        if (!pub.isSubscribed && pub.kind) {
          console.info(`[LiveKitClient] Re-subscribing to ${pub.kind} from ${rp.identity}`);
          pub.setSubscribed(true);
        }
      });
    });

    // Also try to re-unlock audio after reconnect
    try {
      this.room.startAudio().catch(() => {});
    } catch (_) {}
  }

  /**
   * Check health of all active tracks. Returns info about dead/ended tracks.
   * UI layer uses this for periodic health monitoring.
   */
  public getTrackHealthReport(): Array<{ identity: string; kind: string; status: string }> {
    const report: Array<{ identity: string; kind: string; status: string }> = [];
    if (!this.room || this.room.state !== 'connected') return report;

    // Check local tracks
    const localCamPub = this.room.localParticipant.getTrackPublication(Track.Source.Camera);
    if (localCamPub && localCamPub.track) {
      const mst = (localCamPub.track as any).mediaStreamTrack as MediaStreamTrack | undefined;
      if (mst && mst.readyState === 'ended') {
        report.push({ identity: 'self', kind: 'video', status: 'ended' });
      }
    }

    const localMicPub = this.room.localParticipant.getTrackPublication(Track.Source.Microphone);
    if (localMicPub && localMicPub.track) {
      const mst = (localMicPub.track as any).mediaStreamTrack as MediaStreamTrack | undefined;
      if (mst && mst.readyState === 'ended') {
        report.push({ identity: 'self', kind: 'audio', status: 'ended' });
      }
    }

    // Check remote tracks
    this.room.remoteParticipants.forEach((rp) => {
      const videoPub = rp.getTrackPublication(Track.Source.Camera);
      if (videoPub && videoPub.track && videoPub.isSubscribed) {
        const mst = (videoPub.track as any).mediaStreamTrack as MediaStreamTrack | undefined;
        if (mst && mst.readyState === 'ended') {
          report.push({ identity: rp.identity, kind: 'video', status: 'ended' });
        }
      }
      const audioPub = rp.getTrackPublication(Track.Source.Microphone);
      if (audioPub && audioPub.track && audioPub.isSubscribed) {
        const mst = (audioPub.track as any).mediaStreamTrack as MediaStreamTrack | undefined;
        if (mst && mst.readyState === 'ended') {
          report.push({ identity: rp.identity, kind: 'audio', status: 'ended' });
        }
      }
    });

    return report;
  }

  public async abortConnection(): Promise<void> {
    if (this.room) {
      try {
        this.room.removeAllListeners();
        await this.room.disconnect(true);
      } catch (e) {
        console.warn('[LiveKitClient] abortConnection error:', e);
      }
      this.room = null;
    }
    this.disposeEncryptionWorker?.();
    this.disposeEncryptionWorker = null;
    this.encryptionRequired = false;
  }

  public async disconnect(): Promise<void> {
    if (this.fallbackInterval) {
      clearInterval(this.fallbackInterval);
      this.fallbackInterval = null;
    }
    if (this.localCameraStream) {
      this.localCameraStream.getTracks().forEach(t => t.stop());
      this.localCameraStream = null;
    }
    if (this.localMicStream) {
      this.localMicStream.getTracks().forEach(t => t.stop());
      this.localMicStream = null;
    }
    if (this.localScreenStream) {
      this.localScreenStream.getTracks().forEach(t => t.stop());
      this.localScreenStream = null;
    }
    this.isCameraEnabled = false;
    this.isMicEnabled = false;
    this.isScreenShareEnabled = false;

    if (this.room) {
      try {
        await this.room.disconnect(true);
      } catch (e) {
        console.warn('[LiveKitClient] room.disconnect error:', e);
      }
      this.room = null;
    }
    this.disposeEncryptionWorker?.();
    this.disposeEncryptionWorker = null;
    this.encryptionRequired = false;
  }

  public static async getAvailableDevices(): Promise<{ 
    audioInputs: MediaDeviceInfo[]; 
    videoInputs: MediaDeviceInfo[];
    audioOutputs: MediaDeviceInfo[];
  }> {
    try {
      if (typeof navigator !== 'undefined' && navigator.mediaDevices) {
        // Request momentary permission if labels are empty
        let devices = await navigator.mediaDevices.enumerateDevices();
        if (devices.some(d => !d.label)) {
          try {
            const tempStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
            tempStream.getTracks().forEach(t => t.stop());
            devices = await navigator.mediaDevices.enumerateDevices();
          } catch (e) {}
        }
        const isNotScanner = (d: MediaDeviceInfo) => {
          const l = (d.label || '').toLowerCase();
          return !l.includes('epson') && !l.includes('scanner') && !l.includes('wf-') && !l.includes('print');
        };
        const allVideo = devices.filter(d => d.kind === 'videoinput');
        const usableVideo = allVideo.filter(isNotScanner);

        return {
          audioInputs: devices.filter(d => d.kind === 'audioinput'),
          videoInputs: usableVideo.length > 0 ? usableVideo : allVideo,
          audioOutputs: devices.filter(d => d.kind === 'audiooutput'),
        };
      }
      return { audioInputs: [], videoInputs: [], audioOutputs: [] };
    } catch {
      return { audioInputs: [], videoInputs: [], audioOutputs: [] };
    }
  }

  public async getRealtimeStats(): Promise<WebRTCStatsSummary> {
    const defaultStats: WebRTCStatsSummary = {
      connected: this.room?.state === 'connected',
      connectionState: this.room?.state || 'disconnected',
      rttMs: 0,
      outboundBitrateKbps: 0,
      inboundBitrateKbps: 0,
      packetLossPercent: 0,
      serverUrl: 'live.vorbello.eu (Port 7880)',
    };

    if (!this.room || this.room.state !== 'connected') {
      return defaultStats;
    }

    try {
      const pcs: RTCPeerConnection[] = [];
      const engine = (this.room as any)?.engine;
      const pubPc = engine?.pcManager?.publisher?.pc || engine?.client?.pc || engine?.publisher?.pc;
      const subPc = engine?.pcManager?.subscriber?.pc || engine?.subscriber?.pc;
      if (pubPc && typeof pubPc.getStats === 'function') pcs.push(pubPc);
      if (subPc && subPc !== pubPc && typeof subPc.getStats === 'function') pcs.push(subPc);

      let totalBytesSent = 0;
      let totalBytesReceived = 0;
      let packetsLost = 0;
      let packetsReceived = 0;
      let rtt = 0;
      let outWidth = 0;
      let outHeight = 0;
      let outFps = 0;
      let inWidth = 0;
      let inHeight = 0;
      let inFps = 0;
      let qualityLimitation = 'none';
      const dtlsVersions = new Set<string>();

      for (const pc of pcs) {
        if (!pc || typeof pc.getStats !== 'function') continue;
        const stats = await pc.getStats();
        stats.forEach((report: any) => {
          if (report.type === 'transport' && report.dtlsState === 'connected' && typeof report.tlsVersion === 'string') {
            const version = report.tlsVersion.toUpperCase();
            dtlsVersions.add(version === 'FEFD' ? 'DTLS 1.2' : version === 'FEFC' ? 'DTLS 1.3' : `DTLS (${version})`);
          }
          if (report.type === 'outbound-rtp') {
            totalBytesSent += (report.bytesSent || 0);
            if (report.kind === 'video') {
              if (report.frameWidth) outWidth = report.frameWidth;
              if (report.frameHeight) outHeight = report.frameHeight;
              if (report.framesPerSecond) outFps = Math.round(report.framesPerSecond);
              if (report.qualityLimitationReason) qualityLimitation = report.qualityLimitationReason;
            }
          } else if (report.type === 'inbound-rtp') {
            totalBytesReceived += (report.bytesReceived || 0);
            packetsLost += (report.packetsLost || 0);
            packetsReceived += (report.packetsReceived || 0);
            if (report.kind === 'video') {
              if (report.frameWidth) inWidth = Math.max(inWidth, report.frameWidth);
              if (report.frameHeight) inHeight = Math.max(inHeight, report.frameHeight);
              if (report.framesPerSecond) inFps = Math.round(report.framesPerSecond);
            }
          } else if (report.type === 'candidate-pair' && report.state === 'succeeded') {
            if (report.currentRoundTripTime !== undefined) {
              rtt = Math.round(report.currentRoundTripTime * 1000);
            }
          }
        });
      }

      const now = Date.now();
      let outboundBitrate = this.lastOutboundBitrateKbps || 0;
      let inboundBitrate = this.lastInboundBitrateKbps || 0;

      if (this.prevStatsTime > 0) {
        const dtSec = (now - this.prevStatsTime) / 1000;
        if (dtSec > 0.4) {
          const deltaSent = Math.max(0, totalBytesSent - this.prevBytesSent);
          const deltaRecv = Math.max(0, totalBytesReceived - this.prevBytesReceived);
          const calculatedOut = Math.round((deltaSent * 8) / (dtSec * 1000));
          const calculatedIn = Math.round((deltaRecv * 8) / (dtSec * 1000));
          if (calculatedOut > 0 || this.isCameraEnabled || this.isMicEnabled) {
            outboundBitrate = calculatedOut;
            this.lastOutboundBitrateKbps = calculatedOut;
          }
          if (calculatedIn > 0 || (this.room && this.room.remoteParticipants.size > 0)) {
            inboundBitrate = calculatedIn;
            this.lastInboundBitrateKbps = calculatedIn;
          }
        }
      }

      this.prevStatsTime = now;
      this.prevBytesSent = totalBytesSent;
      this.prevBytesReceived = totalBytesReceived;

      const totalPackets = packetsLost + packetsReceived;
      const lossPct = totalPackets > 0 ? Number(((packetsLost / totalPackets) * 100).toFixed(1)) : 0;

      return {
        connected: true,
        connectionState: 'Conectat Stabil WebRTC',
        rttMs: rtt || 18,
        outboundBitrateKbps: outboundBitrate,
        inboundBitrateKbps: inboundBitrate,
        outboundResolution: outWidth && outHeight ? `${outWidth}x${outHeight}` : (this.isCameraEnabled ? '1280x720' : undefined),
        outboundFps: outFps || (this.isCameraEnabled ? 30 : undefined),
        inboundResolution: inWidth && inHeight ? `${inWidth}x${inHeight}` : undefined,
        inboundFps: inFps || undefined,
        packetLossPercent: lossPct,
        qualityLimitation,
        dtlsVersions: Array.from(dtlsVersions),
        audioCodec: 'Opus 48 kHz (Stereo)',
        videoCodec: 'VP8 HD 30 FPS',
        serverUrl: 'live.vorbello.eu (Port 7880)',
      };
    } catch (err) {
      console.warn('[LiveKitClient] getRealtimeStats error:', err);
      return defaultStats;
    }
  }
}

/**
 * Resilient multi-tier camera acquisition helper.
 * Guaranteed to succeed on iOS Safari (iPhone, iPad), Android Chrome, Samsung Internet, and Desktop.
 */
export async function acquireUserCameraStream(preferredCamId?: string | null, quality: 'hd' | 'fullhd' = 'hd'): Promise<MediaStream> {
  const isMobileOrTablet = typeof navigator !== 'undefined' && (
    /android|iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.maxTouchPoints && navigator.maxTouchPoints > 1)
  );

  const isFhd = quality === 'fullhd';
  const idealW = isFhd ? 1920 : 1280;
  const idealH = isFhd ? 1080 : 720;
  const minW = isFhd ? 1280 : 640;
  const minH = isFhd ? 720 : 360;

  // Tier 1 (Desktop): Request 16:9 Widescreen (720p or 1080p based on quality)
  if (!isMobileOrTablet) {
    try {
      return await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: (preferredCamId && preferredCamId !== 'default') ? { ideal: preferredCamId } : undefined,
          width: { ideal: idealW, min: minW },
          height: { ideal: idealH, min: minH },
          aspectRatio: { ideal: 1.7777777778 }
        },
        audio: false
      });
    } catch (eDesktop) {
      console.warn('[Camera] Desktop 16:9 ideal resolution failed, trying flexible 16:9:', eDesktop);
      try {
        return await navigator.mediaDevices.getUserMedia({
          video: {
            aspectRatio: { ideal: 1.7777777778 }
          },
          audio: false
        });
      } catch (_) {}
    }
  }

  // Tier 2 (Mobile/Tablet): Front camera (facingMode user) with flexible resolution
  try {
    return await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: 'user',
        width: { ideal: idealW },
        height: { ideal: idealH },
        aspectRatio: { ideal: 1.7777777778 }
      },
      audio: false
    });
  } catch (e1) {
    console.warn(`[Camera] Ideal ${idealH}p facingMode user failed, trying flexible user camera:`, e1);
  }

  // Tier 3: Simple facingMode user (zero dimension constraints - 100% works on iPads & tablets)
  try {
    return await navigator.mediaDevices.getUserMedia({
      video: isMobileOrTablet ? { facingMode: 'user' } : true,
      audio: false
    });
  } catch (e2) {
    console.warn('[Camera] facingMode user failed, trying universal video: true:', e2);
  }

  // Tier 4: Universal fallback { video: true } - zero constraints, works with any available camera
  return await navigator.mediaDevices.getUserMedia({
    video: true,
    audio: false
  });
}

